import { Auth } from '@mdlinx/shared';
import React, { useEffect } from 'react';
import { ReactElement } from 'react';
import { Box } from 'rebass';
import URLSearchParams from '@ungap/url-search-params';
import * as nodeUrl from 'url';

import { useMDLPortal } from '../contexts/portal';
import { CloseIcon } from '../icons';
import IframeResizer from 'iframe-resizer-react';
import { fetchStoredUtmParams } from '../utility/tracking';

const URL = typeof window === 'undefined' ? nodeUrl.URL : window.URL;

const REDIRECT_DESTINATION_AFTER_LOGIN = 'redirectDestinationAfterLogin';

export type ModalEntryLocation =
  | ''
  | 'LOGIN'
  | 'REGISTRATION'
  | 'ACQUISITION'
  | 'HIGHLIGHTS_ACQUISITION'
  | 'LOGOUT'
  | 'AUTH_ACTION';

export type SetAuthModalState = (args: {
  entryLocation: ModalEntryLocation;
  modalIsDismissable: boolean;
  modalIsOpen: boolean;
}) => any;

export type OpenModal = (args: {
  entryLocation: ModalEntryLocation;
  modalIsDismissable?: boolean;
}) => any;
export type CloseModal = () => any;

type Props = {
  authBaseUrl: string;
  entryLocation: ModalEntryLocation;
  onLoginSucceeded: (successType?: string) => void;
  setAuthModalState: SetAuthModalState;
  modalIsOpen: boolean;
  modalIsDismissable?: boolean;
  inBrowser?: boolean;
};

const useOpenOnQueryParam = (setAuthModalState: SetAuthModalState) => () => {
  const queryParams = new URLSearchParams(window.location.search);

  // Password reset
  if (queryParams.get('oobCode')) {
    setAuthModalState({
      modalIsOpen: true,
      entryLocation: 'AUTH_ACTION',
      modalIsDismissable: true,
    });
    return;
  }

  if (queryParams.get('reg_prompt')) {
    setAuthModalState({
      modalIsOpen: true,
      entryLocation: 'REGISTRATION',
      modalIsDismissable: false,
    });
    return;
  }

  if (queryParams.get('login')) {
    setAuthModalState({
      modalIsOpen: true,
      entryLocation: 'LOGIN',
      modalIsDismissable: true,
    });
    return;
  }

  if (queryParams.get('register')) {
    setAuthModalState({
      modalIsOpen: true,
      entryLocation: 'REGISTRATION',
      modalIsDismissable: true,
    });
    return;
  }
};

export const authIframeUrl = ({
  baseUrl,
  entryLocation,
  searchParams,
  modalIsDismissable,
  issueType,
  inBrowser,
  iframeLocation,
}: {
  baseUrl: string;
  entryLocation: ModalEntryLocation;
  searchParams?: URLSearchParams;
  modalIsDismissable: boolean;
  issueType?: string;
  inBrowser: boolean;
  iframeLocation?: 'in-well' | 'modal';
}) => {
  type QueryParams = [string, string][];
  const configQueryParams: QueryParams = [
    ['modalIsDismissable', modalIsDismissable.toString()],
    ['parentUrl', typeof window === 'undefined' ? '' : window.location.href],
  ];

  if (iframeLocation !== undefined) {
    configQueryParams.push(['iframeLocation', iframeLocation]);
  }

  const storedUtmParams = inBrowser
    ? fetchStoredUtmParams('LAST_TOUCH_REGISTRATION_ATTRIBUTION')
    : undefined;
  const utmQueryParams: QueryParams = storedUtmParams
    ? Object.entries(storedUtmParams).map(([utmKey, utmValue]) => [
        utmKey,
        utmValue ?? '',
      ])
    : [];

  const commonQueryParams = [...configQueryParams, ...utmQueryParams];

  const queryParamsArrayToString = (queryParams: QueryParams) =>
    queryParams.length
      ? queryParams.reduce(
          (queryString, [key, value], i) =>
            `${queryString}${i === 0 ? '?' : '&'}${key}=${encodeURIComponent(
              value
            )}`,
          ''
        )
      : '';

  switch (entryLocation) {
    case 'LOGIN': {
      return `${baseUrl}#/login${queryParamsArrayToString(commonQueryParams)}`;
    }
    case 'REGISTRATION': {
      return `${baseUrl}#/sign-up${queryParamsArrayToString(
        commonQueryParams
      )}`;
    }
    case 'ACQUISITION': {
      return `${baseUrl}#/sign-up/acquisition${queryParamsArrayToString(
        commonQueryParams
      )}`;
    }
    case 'HIGHLIGHTS_ACQUISITION': {
      return `${baseUrl}#/sign-up/highlights-acquisition${queryParamsArrayToString(
        [...commonQueryParams, ['issueType', issueType?.toString() || '']]
      )}`;
    }
    case 'LOGOUT': {
      return `${baseUrl}#/logout${queryParamsArrayToString(commonQueryParams)}`;
    }
    case 'AUTH_ACTION': {
      const oobCode = (searchParams && searchParams.get('oobCode')) || '';
      const mode = (searchParams && searchParams.get('mode')) || '';

      if (!oobCode || !mode) {
        console.error(
          'Attempted to route to password help with missing query params',
          { oobCode, mode }
        );
        return `${baseUrl}#/login${queryParamsArrayToString(
          commonQueryParams
        )}`;
      }

      return `${baseUrl}/#/action${queryParamsArrayToString([
        ...commonQueryParams,
        ['oobCode', oobCode],
        ['mode', mode],
      ])}`;
    }
    default: {
      return `${baseUrl}#/login?modalIsDismissable=${queryParamsArrayToString(
        commonQueryParams
      )}`;
    }
  }
};

export const clearAuthQueryParams = () => {
  if (typeof window === 'undefined') {
    return;
  }

  const currentUrl = new URL((window as Window).location.href);
  ['oobCode', 'mode'].forEach(q => currentUrl.searchParams.delete(q));

  (window as Window).history.replaceState(
    (window as Window).history.state,
    document.title,
    currentUrl.toString()
  );
};

const getInitialHeight = (entryLocation: ModalEntryLocation): number => {
  switch (entryLocation) {
    case 'LOGIN': {
      return 603;
    }
    case 'REGISTRATION': {
      return 569;
    }
    case 'ACQUISITION': {
      return 335;
    }
    case 'HIGHLIGHTS_ACQUISITION': {
      return 335;
    }
    case 'AUTH_ACTION': {
      return 413;
    }
    default: {
      return 603;
    }
  }
};

export const AuthRegModal: React.FC<Props> = ({
  authBaseUrl,
  onLoginSucceeded,
  setAuthModalState,
  modalIsOpen,
  modalIsDismissable = true,
  entryLocation,
  inBrowser = false,
}) => {
  const closeModal = () =>
    setAuthModalState({
      entryLocation: '',
      modalIsOpen: false,
      modalIsDismissable: true,
    });

  useEffect(useOpenOnQueryParam(setAuthModalState), []);
  useEffect(() => {
    const handleAuthIframeMessage = (event: MessageEvent) => {
      if (event.origin !== new URL(authBaseUrl).origin) {
        // Message did not originate from auth iframe
        return;
      }

      switch (event.data) {
        case Auth.loginSucceeded: {
          clearAuthQueryParams();
          onLoginSucceeded();
          closeModal();
          return;
        }
        case Auth.highlightsLoginSucceeded: {
          clearAuthQueryParams();
          onLoginSucceeded(Auth.highlightsLoginSucceeded);
          return;
        }
        case Auth.abortLogin: {
          closeModal();
          return;
        }
        case Auth.loginLinkSent: {
          window.localStorage.setItem(
            REDIRECT_DESTINATION_AFTER_LOGIN,
            window.location.href
          );
          return;
        }
        case Auth.loginWithLinkSucceeded: {
          const redirectTo = window.localStorage.getItem(
            REDIRECT_DESTINATION_AFTER_LOGIN
          );
          if (redirectTo) {
            window.location.href = redirectTo;
            return;
          } else {
            clearAuthQueryParams();
            onLoginSucceeded();
            closeModal();
            return;
          }
        }
        default: {
          return;
        }
      }
    };

    window.addEventListener('message', handleAuthIframeMessage);

    return function cleanup() {
      window.removeEventListener('message', handleAuthIframeMessage);
    };
  }, []);

  const pageSearchParams =
    !!inBrowser && new URL(window.location.href).searchParams;
  const issueType = !!inBrowser
    ? new URL(window.location.href).pathname.split('/')[2]
    : '';

  const iframeUrl = authIframeUrl({
    baseUrl: authBaseUrl,
    entryLocation,
    ...(pageSearchParams ? { searchParams: pageSearchParams } : {}),
    modalIsDismissable,
    issueType,
    inBrowser,
  });

  return (
    <Modal open={modalIsOpen}>
      <IframeResizer
        src={iframeUrl}
        style={{
          width: '1px',
          minWidth: '100%',
          border: 0,
          height: getInitialHeight(entryLocation),
        }}
        checkOrigin={false}
      />
    </Modal>
  );
};

export type ModalProps = {
  open: boolean;
  children?: ReactElement;
};
export const Modal: React.FC<ModalProps> = ({ open, children }) => {
  const Portal = useMDLPortal();

  return (
    Portal && (
      <Portal>
        {open && (
          <Box
            sx={
              {
                position: 'fixed',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                height: '100%',
                top: 0,
                left: 0,
                backgroundColor: 'rgba(28, 39, 63, 0.6)',
                zIndex: 1299,
                overflowY: 'auto',
              } as const
            }
          >
            <Box
              sx={
                {
                  height: ['100%', 'auto'],
                  width: ['100%'],
                  maxWidth: ['100%', '494px'],
                  backgroundColor: '#fff',
                  borderRadius: ['0px', '8px'],
                  boxShadow: '5px 8px 12px 0 rgba(0, 0, 0, 0.4)',
                  top: ['71px', '71px', '122px'],
                  padding: '5px',
                  position: 'absolute',
                } as const
              }
              mb="20px"
              onClick={e => e.stopPropagation()}
            >
              {children}
            </Box>
          </Box>
        )}
      </Portal>
    )
  );
};

export const ModalCloseButton = ({
  onClose,
  modalIsDismissable,
}: {
  onClose: () => void;
  modalIsDismissable: boolean;
}) => (
  <Box
    sx={
      {
        position: 'absolute',
        right: '16px',
        top: '16px',
        cursor: 'pointer',
        zIndex: 10,
        backgroundColor: '#fff',
      } as const
    }
    onClick={onClose}
  >
    {modalIsDismissable && <CloseIcon height="16px" width="16px" />}
  </Box>
);
